"use client"

import { useMemo, useState } from "react"
import Link from "next/link"
import {
  AlertCircle,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  Check,
  CheckCircle2,
  Copy,
  Eye,
  Globe2,
  GripVertical,
  ImageIcon,
  Lightbulb,
  LoaderCircle,
  Play,
  Plus,
  Share2,
  Sparkles,
  Trash2,
} from "lucide-react"
import { Footer } from "@/components/layout/footer"
import { Header } from "@/components/layout/header"
import { TurnstileWidget } from "@/components/security/turnstile-widget"
import { trackProductEvent } from "@/lib/analytics/client"
import styles from "./create-game.module.css"

interface ItemDraft {
  id: string
  name: string
  value: string
  image: string
  description: string
}

interface CreatedGame {
  id: string
  slug: string
  title: string
  url: string
}

const MAX_ITEMS = 200
const MIN_ITEMS = 4

function createEmptyItem(id = crypto.randomUUID()): ItemDraft {
  return {
    id,
    name: "",
    value: "",
    image: "",
    description: "",
  }
}

function getPreviewImage(value: string) {
  try {
    const url = new URL(value.trim())
    if (url.protocol !== "http:" && url.protocol !== "https:") return null
    return url.href
  } catch {
    return null
  }
}

function formatPreviewValue(
  rawValue: string,
  unit: string,
  usePrefix: boolean,
) {
  if (!rawValue.trim()) return "Value"
  const numericValue = Number(rawValue.replaceAll(",", ""))
  if (!Number.isFinite(numericValue)) return rawValue

  const formatted = new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 2,
  }).format(numericValue)

  if (!unit.trim()) return formatted
  return usePrefix ? `${unit}${formatted}` : `${formatted} ${unit}`
}

function PreviewArtwork({
  item,
  index,
}: {
  item: ItemDraft
  index: number
}) {
  const imageUrl = getPreviewImage(item.image)
  const label = item.name.trim() || `Item ${index + 1}`

  return (
    <div
      className={styles.previewArtwork}
      style={
        imageUrl
          ? { backgroundImage: `url("${imageUrl.replaceAll('"', "%22")}")` }
          : undefined
      }
      aria-hidden="true"
    >
      {!imageUrl ? (
        <>
          <ImageIcon />
          <span>{label.slice(0, 1).toUpperCase()}</span>
        </>
      ) : null}
    </div>
  )
}

export default function CreateCustomGamePage() {
  const [title, setTitle] = useState("")
  const [description, setDescription] = useState("")
  const [valueLabel, setValueLabel] = useState("")
  const [valueUnit, setValueUnit] = useState("")
  const [valuePrefix, setValuePrefix] = useState(false)
  const [items, setItems] = useState<ItemDraft[]>([
    createEmptyItem("initial-item-1"),
    createEmptyItem("initial-item-2"),
    createEmptyItem("initial-item-3"),
    createEmptyItem("initial-item-4"),
  ])
  const [draggedItemId, setDraggedItemId] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState("")
  const [turnstileToken, setTurnstileToken] = useState("")
  const [turnstileResetKey, setTurnstileResetKey] = useState(0)
  const [createdGame, setCreatedGame] = useState<CreatedGame | null>(null)
  const [shareFeedback, setShareFeedback] = useState("")

  const previewItems = useMemo(() => items.slice(0, 2), [items])
  const usableItemCount = useMemo(
    () => items.filter((item) => item.name.trim() && item.value.trim()).length,
    [items],
  )

  const addItem = () => {
    if (items.length >= MAX_ITEMS) return
    setItems((currentItems) => [...currentItems, createEmptyItem()])
  }

  const removeItem = (id: string) => {
    if (items.length <= MIN_ITEMS) return
    setItems((currentItems) => currentItems.filter((item) => item.id !== id))
  }

  const updateItem = (
    id: string,
    field: keyof ItemDraft,
    value: string,
  ) => {
    setItems((currentItems) =>
      currentItems.map((item) =>
        item.id === id ? { ...item, [field]: value } : item,
      ),
    )
  }

  const moveItemToIndex = (id: string, nextIndex: number) => {
    setItems((currentItems) => {
      const currentIndex = currentItems.findIndex((item) => item.id === id)
      const boundedIndex = Math.max(
        0,
        Math.min(nextIndex, currentItems.length - 1),
      )

      if (currentIndex < 0 || currentIndex === boundedIndex) return currentItems

      const reordered = [...currentItems]
      const [movedItem] = reordered.splice(currentIndex, 1)
      reordered.splice(boundedIndex, 0, movedItem)
      return reordered
    })
  }

  const moveItemBefore = (movingId: string, targetId: string) => {
    setItems((currentItems) => {
      const currentIndex = currentItems.findIndex(
        (item) => item.id === movingId,
      )
      const targetIndex = currentItems.findIndex((item) => item.id === targetId)
      if (
        currentIndex < 0 ||
        targetIndex < 0 ||
        currentIndex === targetIndex
      ) {
        return currentItems
      }

      const reordered = [...currentItems]
      const [movedItem] = reordered.splice(currentIndex, 1)
      const adjustedTarget =
        currentIndex < targetIndex ? targetIndex - 1 : targetIndex
      reordered.splice(adjustedTarget, 0, movedItem)
      return reordered
    })
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    setError("")

    if (!title.trim()) {
      setError("Please enter a game title")
      return
    }
    if (!valueLabel.trim()) {
      setError("Please enter a value label, such as price or population")
      return
    }

    const validItems = items.filter(
      (item) => item.name.trim() && item.value.trim(),
    )
    if (validItems.length < MIN_ITEMS) {
      setError(
        `Add names and values for at least ${MIN_ITEMS} items before publishing`,
      )
      return
    }

    for (const item of validItems) {
      if (!Number.isFinite(Number(item.value.replaceAll(",", "")))) {
        setError(`"${item.name}" needs a numeric value`)
        return
      }
    }

    setCreating(true)

    try {
      const response = await fetch("/api/custom-games", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: title.trim(),
          description: description.trim() || null,
          valueLabel: valueLabel.trim(),
          valueUnit: valueUnit.trim() || null,
          valuePrefix,
          turnstileToken,
          items: validItems.map((item) => ({
            name: item.name.trim(),
            value: Number(item.value.replaceAll(",", "")),
            image: item.image.trim() || null,
            description: item.description.trim() || null,
          })),
        }),
      })

      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.error || "Failed to create game")
      }

      const data = await response.json()
      trackProductEvent("custom_game_created", {
        customGameId: data.id,
        properties: {
          itemCount: validItems.length,
          hasDescription: Boolean(description.trim()),
        },
      })
      setCreatedGame({
        id: data.id,
        slug: data.slug,
        title: title.trim(),
        url: new URL(`/custom/${data.slug}`, window.location.origin).href,
      })
      setCreating(false)
      window.scrollTo({ top: 0, behavior: "smooth" })
    } catch (caughtError) {
      setError(
        caughtError instanceof Error
          ? caughtError.message
          : "Failed to create game",
      )
      setCreating(false)
      setTurnstileToken("")
      setTurnstileResetKey((key) => key + 1)
    }
  }

  const copyCreatedGameLink = async () => {
    if (!createdGame) return

    try {
      await navigator.clipboard.writeText(createdGame.url)
      setShareFeedback("Link copied")
      trackProductEvent("custom_game_shared", {
        customGameId: createdGame.id,
        properties: { method: "copy_after_creation" },
      })
    } catch {
      setShareFeedback("Select the URL and copy it manually")
    }
  }

  const shareCreatedGame = async () => {
    if (!createdGame) return

    if (!navigator.share) {
      await copyCreatedGameLink()
      return
    }

    try {
      await navigator.share({
        title: createdGame.title,
        text: `Play my ${createdGame.title} Higher Lower game`,
        url: createdGame.url,
      })
      setShareFeedback("Game shared")
      trackProductEvent("custom_game_shared", {
        customGameId: createdGame.id,
        properties: { method: "native_after_creation" },
      })
    } catch (shareError) {
      if (
        shareError instanceof DOMException &&
        shareError.name === "AbortError"
      ) {
        return
      }
      setShareFeedback("Sharing was unavailable. Copy the link instead")
    }
  }

  return (
    <div className={`public-site ${styles.page}`}>
      <Header />

      <main className={styles.main}>
        {createdGame ? (
          <section
            className={styles.successView}
            aria-labelledby="custom-game-published-title"
          >
            <span className={styles.successIcon} aria-hidden="true">
              <Check />
            </span>
            <p className={styles.successEyebrow}>Game published</p>
            <h1 id="custom-game-published-title">
              Your game is ready to share
            </h1>
            <p className={styles.successIntro}>
              Send this link to anyone you want to challenge. They can open it
              and start playing immediately.
            </p>

            <div className={styles.shareCard}>
              <label htmlFor="created-game-url">Shareable game URL</label>
              <div className={styles.shareUrlRow}>
                <input
                  id="created-game-url"
                  type="text"
                  readOnly
                  value={createdGame.url}
                  onFocus={(event) => event.currentTarget.select()}
                />
                <button type="button" onClick={copyCreatedGameLink}>
                  <Copy aria-hidden="true" />
                  Copy link
                </button>
              </div>
              <p className={styles.shareFeedback} aria-live="polite">
                {shareFeedback ||
                  "This URL stays the same, so you can share it anywhere."}
              </p>
            </div>

            <div className={styles.successActions}>
              <button
                type="button"
                className={styles.shareButton}
                onClick={shareCreatedGame}
              >
                <Share2 aria-hidden="true" />
                Share game
              </button>
              <Link
                href={`/custom/${createdGame.slug}`}
                className={styles.playButton}
              >
                Play your game
                <span aria-hidden="true">
                  <Play />
                </span>
              </Link>
            </div>

            <Link href="/custom" className={styles.successBackLink}>
              <ArrowLeft aria-hidden="true" />
              Back to custom games
            </Link>
          </section>
        ) : (
          <div className={styles.pageInner}>
            <Link href="/custom" className={styles.backLink}>
              <ArrowLeft aria-hidden="true" />
              Back to custom games
            </Link>

            <header className={styles.pageHeader}>
              <span className={styles.pageIcon} aria-hidden="true">
                <Sparkles />
              </span>
              <div>
                <h1>Create your game</h1>
                <p>
                  Build a custom Higher Lower game with your own items and share
                  it with the world.
                </p>
              </div>
            </header>

            <form className={styles.builderGrid} onSubmit={handleSubmit}>
            <section
              className={`${styles.editorPanel} ${styles.detailsPanel}`}
              aria-labelledby="game-details-heading"
            >
              <div className={styles.sectionHeading}>
                <span aria-hidden="true">
                  <Sparkles />
                </span>
                <div>
                  <h2 id="game-details-heading">Game details</h2>
                  <p>Give players the context they need before they start.</p>
                </div>
              </div>

              <div className={styles.field}>
                <div className={styles.labelRow}>
                  <label htmlFor="game-title">
                    Title <em>*</em>
                  </label>
                  <span>{title.length}/100</span>
                </div>
                <input
                  id="game-title"
                  type="text"
                  value={title}
                  onChange={(event) => setTitle(event.target.value)}
                  placeholder="Fast Food Prices, Movie Ratings, Country Populations"
                  maxLength={100}
                  autoComplete="off"
                  required
                />
              </div>

              <div className={styles.field}>
                <div className={styles.labelRow}>
                  <label htmlFor="game-description">Description</label>
                  <span>{description.length}/300</span>
                </div>
                <textarea
                  id="game-description"
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                  placeholder="A short description of your game"
                  rows={3}
                  maxLength={300}
                />
              </div>

              <div className={styles.metricGrid}>
                <div className={styles.field}>
                  <label htmlFor="value-label">
                    Value label <em>*</em>
                  </label>
                  <input
                    id="value-label"
                    type="text"
                    value={valueLabel}
                    onChange={(event) => setValueLabel(event.target.value)}
                    placeholder="Price, rating, population"
                    maxLength={50}
                    autoComplete="off"
                    required
                  />
                </div>

                <fieldset className={styles.prefixField}>
                  <legend>Unit position</legend>
                  <div className={styles.segmentedControl}>
                    <button
                      type="button"
                      className={valuePrefix ? styles.segmentActive : undefined}
                      onClick={() => setValuePrefix(true)}
                      aria-pressed={valuePrefix}
                    >
                      Before
                    </button>
                    <button
                      type="button"
                      className={!valuePrefix ? styles.segmentActive : undefined}
                      onClick={() => setValuePrefix(false)}
                      aria-pressed={!valuePrefix}
                    >
                      After
                    </button>
                  </div>
                </fieldset>

                <div className={styles.field}>
                  <label htmlFor="value-unit">
                    Unit <span>(optional)</span>
                  </label>
                  <input
                    id="value-unit"
                    type="text"
                    value={valueUnit}
                    onChange={(event) => setValueUnit(event.target.value)}
                    placeholder="$, %, kg"
                    maxLength={10}
                    autoComplete="off"
                  />
                </div>
              </div>
            </section>

            <aside className={styles.previewColumn}>
              <section
                className={styles.previewPanel}
                aria-labelledby="live-preview-heading"
              >
                <div className={styles.sectionHeading}>
                  <span aria-hidden="true">
                    <Eye />
                  </span>
                  <div>
                    <h2 id="live-preview-heading">Live preview</h2>
                    <p>This is how your first comparison will look.</p>
                  </div>
                </div>

                <div className={styles.gamePreview}>
                  <div className={styles.previewCards}>
                    {previewItems.map((item, index) => (
                      <article className={styles.previewCard} key={item.id}>
                        <PreviewArtwork item={item} index={index} />
                        <div className={styles.previewCardBody}>
                          <h3>{item.name.trim() || `Item ${index + 1}`}</h3>
                          <strong>
                            {formatPreviewValue(
                              item.value,
                              valueUnit,
                              valuePrefix,
                            )}
                          </strong>
                          <span>{valueLabel.trim() || "Your metric"}</span>
                        </div>
                      </article>
                    ))}
                    <span className={styles.versusDisk} aria-hidden="true">
                      VS
                    </span>
                  </div>

                  <div className={styles.previewControls} aria-hidden="true">
                    <span className={styles.directionButton}>
                      <ArrowUp />
                    </span>
                    <span className={styles.previewChoice}>Higher</span>
                    <small>or</small>
                    <span className={styles.previewChoice}>Lower</span>
                    <span className={styles.directionButton}>
                      <ArrowDown />
                    </span>
                  </div>
                </div>

                <div className={styles.previewHint}>
                  <Lightbulb aria-hidden="true" />
                  <p>
                    <strong>
                      {usableItemCount >= MIN_ITEMS
                        ? `${usableItemCount} items ready`
                        : `${MIN_ITEMS - usableItemCount} more complete ${
                            MIN_ITEMS - usableItemCount === 1 ? "item" : "items"
                          } needed`}
                    </strong>
                    Preview uses the first two items. Reorder them with the
                    handles.
                  </p>
                </div>
              </section>

              <section
                className={styles.tipsPanel}
                aria-labelledby="tips-heading"
              >
                <div className={styles.sectionHeading}>
                  <span aria-hidden="true">
                    <Lightbulb />
                  </span>
                  <div>
                    <h2 id="tips-heading">Tips for a great game</h2>
                  </div>
                </div>
                <ul>
                  <li>
                    <CheckCircle2 aria-hidden="true" />
                    Use clear, specific titles and descriptions
                  </li>
                  <li>
                    <CheckCircle2 aria-hidden="true" />
                    Keep one consistent unit of measurement
                  </li>
                  <li>
                    <CheckCircle2 aria-hidden="true" />
                    Add image URLs to make comparisons more engaging
                  </li>
                  <li>
                    <CheckCircle2 aria-hidden="true" />
                    Start with 4 items. Use 5-20 for longer games
                  </li>
                </ul>
              </section>
            </aside>

            <section
              className={`${styles.editorPanel} ${styles.itemsPanel}`}
              aria-labelledby="items-heading"
            >
              <div className={styles.itemsHeader}>
                <div className={styles.sectionHeading}>
                  <span aria-hidden="true">
                    <GripVertical />
                  </span>
                  <div>
                    <h2 id="items-heading">Items</h2>
                    <p>Add the things players will compare.</p>
                  </div>
                </div>

                <button
                  type="button"
                  className={styles.addButton}
                  onClick={addItem}
                  disabled={items.length >= MAX_ITEMS}
                >
                  <Plus aria-hidden="true" />
                  Add item
                </button>
              </div>

              <div className={styles.itemList}>
                {items.map((item, index) => (
                  <article
                    key={item.id}
                    className={`${styles.itemRow} ${
                      draggedItemId === item.id ? styles.itemDragging : ""
                    }`}
                    onDragOver={(event) => {
                      if (!draggedItemId || draggedItemId === item.id) return
                      event.preventDefault()
                    }}
                    onDrop={(event) => {
                      event.preventDefault()
                      if (draggedItemId) {
                        moveItemBefore(draggedItemId, item.id)
                        setDraggedItemId(null)
                      }
                    }}
                  >
                    <div className={styles.itemIdentity}>
                      <button
                        type="button"
                        className={styles.dragHandle}
                        draggable
                        aria-label={`Move item ${index + 1}. Use the up and down arrow keys or drag to reorder`}
                        onDragStart={(event) => {
                          setDraggedItemId(item.id)
                          event.dataTransfer.effectAllowed = "move"
                          event.dataTransfer.setData("text/plain", item.id)
                        }}
                        onDragEnd={() => setDraggedItemId(null)}
                        onKeyDown={(event) => {
                          if (event.key === "ArrowUp") {
                            event.preventDefault()
                            moveItemToIndex(item.id, index - 1)
                          }
                          if (event.key === "ArrowDown") {
                            event.preventDefault()
                            moveItemToIndex(item.id, index + 1)
                          }
                        }}
                      >
                        <GripVertical aria-hidden="true" />
                      </button>
                      <span>{index + 1}</span>
                    </div>

                    <div className={styles.itemFields}>
                      <div className={styles.itemField}>
                        <label htmlFor={`item-name-${item.id}`}>
                          Item name <em>*</em>
                        </label>
                        <input
                          id={`item-name-${item.id}`}
                          type="text"
                          value={item.name}
                          onChange={(event) =>
                            updateItem(item.id, "name", event.target.value)
                          }
                          placeholder="Tokyo"
                          maxLength={100}
                          autoComplete="off"
                        />
                      </div>

                      <div className={styles.itemField}>
                        <label htmlFor={`item-value-${item.id}`}>
                          Value <em>*</em>
                        </label>
                        <input
                          id={`item-value-${item.id}`}
                          type="text"
                          inputMode="decimal"
                          value={item.value}
                          onChange={(event) =>
                            updateItem(item.id, "value", event.target.value)
                          }
                          placeholder="37,400,068"
                          autoComplete="off"
                        />
                      </div>

                      <div className={styles.itemField}>
                        <label htmlFor={`item-image-${item.id}`}>
                          Image URL <span>(optional)</span>
                        </label>
                        <input
                          id={`item-image-${item.id}`}
                          type="url"
                          value={item.image}
                          onChange={(event) =>
                            updateItem(item.id, "image", event.target.value)
                          }
                          placeholder="https://example.com/image.jpg"
                          maxLength={500}
                          autoComplete="url"
                        />
                      </div>

                      <div className={styles.itemField}>
                        <label htmlFor={`item-description-${item.id}`}>
                          Description <span>(optional)</span>
                        </label>
                        <input
                          id={`item-description-${item.id}`}
                          type="text"
                          value={item.description}
                          onChange={(event) =>
                            updateItem(
                              item.id,
                              "description",
                              event.target.value,
                            )
                          }
                          placeholder="The capital of Japan"
                          maxLength={200}
                          autoComplete="off"
                        />
                      </div>
                    </div>

                    <button
                      type="button"
                      className={styles.deleteButton}
                      onClick={() => removeItem(item.id)}
                      disabled={items.length <= MIN_ITEMS}
                      aria-label={`Delete item ${index + 1}`}
                    >
                      <Trash2 aria-hidden="true" />
                    </button>
                  </article>
                ))}
              </div>

              <div className={styles.itemListFooter}>
                <span>Minimum of {MIN_ITEMS} complete items required</span>
                <strong>
                  {items.length}/{MAX_ITEMS} items
                </strong>
              </div>

              <button
                type="button"
                className={styles.addAnotherButton}
                onClick={addItem}
                disabled={items.length >= MAX_ITEMS}
              >
                <Plus aria-hidden="true" />
                Add another item
              </button>
            </section>

            <section className={styles.submissionPanel}>
              {error ? (
                <div className={styles.errorMessage} role="alert">
                  <AlertCircle aria-hidden="true" />
                  <span>{error}</span>
                </div>
              ) : null}

              <div className={styles.turnstileWrap}>
                <TurnstileWidget
                  onTokenChange={setTurnstileToken}
                  resetKey={turnstileResetKey}
                  theme="light"
                />
              </div>

              <button
                type="submit"
                className={styles.submitButton}
                disabled={creating || !turnstileToken}
              >
                <span>
                  {creating ? "Creating game..." : "Create and share game"}
                </span>
                <i aria-hidden="true">
                  {creating ? (
                    <LoaderCircle className={styles.spinner} />
                  ) : (
                    <ArrowRight />
                  )}
                </i>
              </button>

              <p className={styles.publishNote}>
                <Globe2 aria-hidden="true" />
                Your game will be public and playable by anyone.
              </p>
            </section>
            </form>
          </div>
        )}
      </main>

      <Footer />
    </div>
  )
}
